import { useState, useMemo, useRef, useCallback } from 'react';
import { PlaceSummary } from '@/hooks/usePlaces';
import { PlaceCard } from '../places/PlaceCard';
import { PlaceCardSkeleton } from '../places/PlaceCardSkeleton';
import { cn } from '@/lib/utils';
import { ChevronUp, MapPin, X, RefreshCw } from 'lucide-react';
import { useMapStore, BottomSheetState } from '@/store/mapStore';

interface MobileBottomSheetProps {
  places: PlaceSummary[];
  isLoading?: boolean;
  onPlaceSelect: (place: PlaceSummary) => void;
  onClose: () => void;
  onRefresh?: () => void;
}

// Sheet height states
const COLLAPSED_HEIGHT = 80;
const HALF_HEIGHT = typeof window !== 'undefined' ? window.innerHeight * 0.45 : 350;
const FULL_HEIGHT = typeof window !== 'undefined' ? window.innerHeight - 160 : 500;

// Spring physics constants
const RUBBER_BAND_FACTOR = 0.3;
const VELOCITY_THRESHOLD = 0.5;

export const MobileBottomSheet = ({ 
  places, 
  isLoading,
  onPlaceSelect, 
  onClose,
  onRefresh
}: MobileBottomSheetProps) => {
  const { bottomSheetState: sheetState, setBottomSheetState: setSheetState } = useMapStore();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const startTime = useRef(0);
  const currentHeight = useRef(COLLAPSED_HEIGHT);
  const lastVelocity = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isPullingToRefresh = useRef(false);

  // Pull-to-refresh constants
  const PULL_THRESHOLD = 80;
  const MAX_PULL = 120;

  // Sort by distance
  const sortedPlaces = useMemo(() => {
    return [...places].sort((a, b) => {
      if (a.distance_km === null) return 1;
      if (b.distance_km === null) return -1;
      return a.distance_km - b.distance_km;
    });
  }, [places]);

  const getHeightForState = (state: BottomSheetState) => {
    switch (state) {
      case 'minimized': return 0;
      case 'collapsed': return COLLAPSED_HEIGHT;
      case 'half': return HALF_HEIGHT;
      case 'full': return FULL_HEIGHT;
    }
  };

  // Rubber band effect for over-scroll
  const applyRubberBand = useCallback((offset: number, maxDrag: number, minDrag: number) => {
    if (offset > maxDrag) {
      const overscroll = offset - maxDrag;
      return maxDrag + (overscroll * RUBBER_BAND_FACTOR);
    }
    if (offset < minDrag) {
      const overscroll = minDrag - offset;
      return minDrag - (overscroll * RUBBER_BAND_FACTOR);
    }
    return offset;
  }, []);

  const snapToState = useCallback((velocity: number, currentDragOffset: number) => {
    const baseHeight = getHeightForState(sheetState);
    const newHeight = baseHeight - currentDragOffset;
    
    // Velocity-based snapping
    if (velocity < -VELOCITY_THRESHOLD) {
      // Drag up (negative velocity = going up)
      if (sheetState === 'minimized') setSheetState('collapsed');
      else if (sheetState === 'collapsed') setSheetState('half');
      else if (sheetState === 'half') setSheetState('full');
    } else if (velocity > VELOCITY_THRESHOLD) {
      // Drag down
      if (sheetState === 'full') setSheetState('half');
      else if (sheetState === 'half') setSheetState('collapsed');
      else if (sheetState === 'collapsed') setSheetState('minimized');
    } else {
      // Snap to nearest state
      const distances = [
        { state: 'minimized' as BottomSheetState, dist: Math.abs(newHeight - 0) },
        { state: 'collapsed' as BottomSheetState, dist: Math.abs(newHeight - COLLAPSED_HEIGHT) },
        { state: 'half' as BottomSheetState, dist: Math.abs(newHeight - HALF_HEIGHT) },
        { state: 'full' as BottomSheetState, dist: Math.abs(newHeight - FULL_HEIGHT) },
      ];
      const nearest = distances.sort((a, b) => a.dist - b.dist)[0];
      setSheetState(nearest.state);
    }
    
    setDragOffset(0);
  }, [sheetState, setSheetState]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    startTime.current = Date.now();
    currentHeight.current = getHeightForState(sheetState);
    lastVelocity.current = 0;
    setIsDragging(true);
  }, [sheetState]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    // Calculate velocity
    const timeDiff = Date.now() - startTime.current;
    if (timeDiff > 0) {
      lastVelocity.current = diff / timeDiff * 10;
    }
    
    // Apply rubber band effect at boundaries
    const maxDrag = currentHeight.current;
    const minDrag = -(FULL_HEIGHT - currentHeight.current);
    
    const boundedOffset = applyRubberBand(diff, maxDrag, minDrag);
    setDragOffset(boundedOffset);
  }, [isDragging, applyRubberBand]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    snapToState(lastVelocity.current, dragOffset);
    setIsDragging(false);
  }, [isDragging, dragOffset, snapToState]);

  const handleHeaderClick = useCallback(() => {
    if (sheetState === 'minimized') {
      setSheetState('collapsed');
    } else if (sheetState === 'collapsed') {
      setSheetState('half');
    } else if (sheetState === 'half') {
      setSheetState('full');
    }
  }, [sheetState, setSheetState]);

  const handleMinimize = useCallback(() => {
    setSheetState('minimized');
  }, [setSheetState]);

  // Pull-to-refresh handlers
  const handlePullStart = useCallback((e: React.TouchEvent) => {
    if (sheetState === 'collapsed' || !scrollContainerRef.current) return;
    
    const scrollTop = scrollContainerRef.current.scrollTop;
    if (scrollTop <= 0) {
      isPullingToRefresh.current = true;
      startY.current = e.touches[0].clientY;
    }
  }, [sheetState]);

  const handlePullMove = useCallback((e: React.TouchEvent) => {
    if (!isPullingToRefresh.current || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    if (diff > 0) {
      // Apply resistance
      const resistedPull = diff * 0.5;
      setPullDistance(Math.min(resistedPull, MAX_PULL));
    }
  }, [isRefreshing, MAX_PULL]);

  const handlePullEnd = useCallback(async () => {
    if (!isPullingToRefresh.current) return;
    isPullingToRefresh.current = false;
    
    if (pullDistance >= PULL_THRESHOLD && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(0);
      
      // Simulate refresh or call actual refresh
      await onRefresh();
      
      // Minimum refresh time for UX
      await new Promise(resolve => setTimeout(resolve, 600));
      setIsRefreshing(false);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, PULL_THRESHOLD, onRefresh]);

  const baseHeight = getHeightForState(sheetState);
  const displayHeight = isDragging ? baseHeight - dragOffset : baseHeight;

  // Minimized state - floating button
  if (sheetState === 'minimized') {
    return (
      <button
        onClick={() => setSheetState('collapsed')}
        className={cn(
          "fixed left-4 z-40 bg-card border border-border/50 rounded-full",
          "px-4 py-2 shadow-lg flex items-center gap-2",
          "hover:bg-muted transition-all duration-300",
          "press-effect hover:shadow-xl hover:-translate-y-0.5"
        )}
        style={{ bottom: 'calc(84px + env(safe-area-inset-bottom))' }}
      >
        <ChevronUp className="w-4 h-4" />
        <span className="text-sm font-medium">{sortedPlaces.length} lieu{sortedPlaces.length > 1 ? 'x' : ''}</span>
      </button>
    );
  }

  return (
    <div 
      className={cn(
        'fixed left-0 right-0 bg-card rounded-t-3xl z-40',
        'shadow-[0_-10px_40px_rgba(0,0,0,0.15)]',
        // Spring transition when not dragging
        !isDragging && 'transition-all duration-500 spring-transition'
      )}
      style={{ 
        bottom: 'calc(72px + env(safe-area-inset-bottom))',
        height: `${displayHeight}px`,
      }}
      role="dialog"
      aria-label="Lieux à proximité"
    >
      {/* Drag handle area */}
      <div 
        className="flex flex-col items-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleHeaderClick}
      >
        {/* Animated handle bar */}
        <div className={cn(
          'w-12 h-1.5 rounded-full mb-3',
          'transition-all duration-300 ease-out',
          isDragging 
            ? 'bg-primary w-16 scale-y-125' 
            : 'bg-muted-foreground/30 hover:bg-muted-foreground/50',
          sheetState === 'collapsed' && !isDragging && 'animate-drag-hint'
        )} />
        
        {/* Header */}
        <div className="w-full flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-foreground text-base">Lieux à proximité</h2>
            {sheetState === 'collapsed' && (
              <ChevronUp className="w-4 h-4 text-muted-foreground animate-pulse-subtle" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm text-muted-foreground font-medium",
              "transition-all duration-300"
            )}>
              {sortedPlaces.length} lieu{sortedPlaces.length > 1 ? 'x' : ''}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
              className="p-1.5 rounded-full hover:bg-muted transition-all duration-200 press-effect"
              aria-label="Masquer le panneau"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Content with smooth scroll and pull-to-refresh */}
      <div 
        ref={scrollContainerRef}
        className={cn(
          'px-4 pb-4 overflow-hidden relative',
          sheetState !== 'collapsed' && 'overflow-y-auto custom-scrollbar'
        )} 
        style={{ height: `calc(100% - 56px)` }}
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
      >
        {/* Pull-to-refresh indicator */}
        {(pullDistance > 0 || isRefreshing) && sheetState !== 'collapsed' && (
          <div 
            className="flex justify-center items-center transition-all duration-300"
            style={{ 
              height: isRefreshing ? 48 : Math.min(pullDistance, MAX_PULL),
              opacity: isRefreshing ? 1 : Math.min(pullDistance / PULL_THRESHOLD, 1)
            }}
          >
            <RefreshCw 
              className={cn(
                "w-5 h-5 text-primary transition-transform duration-300",
                isRefreshing && "animate-spin",
                pullDistance >= PULL_THRESHOLD && !isRefreshing && "text-success"
              )}
              style={{ 
                transform: isRefreshing 
                  ? undefined 
                  : `rotate(${Math.min(pullDistance / PULL_THRESHOLD, 1) * 180}deg)` 
              }}
            />
            {pullDistance >= PULL_THRESHOLD && !isRefreshing && (
              <span className="ml-2 text-xs text-success font-medium">Relâchez pour rafraîchir</span>
            )}
          </div>
        )}

        {isLoading || isRefreshing ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <PlaceCardSkeleton key={i} compact />
            ))}
          </div>
        ) : sortedPlaces.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <MapPin className="w-8 h-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground text-center">
              Aucun lieu trouvé dans cette zone
            </p>
          </div>
        ) : sheetState === 'collapsed' ? (
          // Collapsed preview
          <div 
            className="flex items-center justify-between cursor-pointer p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors duration-200"
            onClick={() => sortedPlaces[0] && onPlaceSelect(sortedPlaces[0])}
          >
            <span className="font-medium text-sm truncate">{sortedPlaces[0]?.name}</span>
            <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
              {sortedPlaces[0]?.distance_km !== null ? `${sortedPlaces[0]?.distance_km} km` : sortedPlaces[0]?.city}
            </span>
          </div>
        ) : (
          // Full list with staggered animation
          <div className="space-y-2">
            {sortedPlaces.slice(0, 20).map((place, index) => (
              <div
                key={place.id}
                className="animate-fade-in-gentle opacity-0"
                style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'forwards' }}
              >
                <PlaceCard
                  place={place}
                  onClick={() => onPlaceSelect(place)}
                  compact
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
